import React, { FC, useEffect, useState } from "react";
import { RHRHeaderProps } from "@RHCommerceDev/container-rhr-header/types";
import { useLocation } from "react-router-dom";
import { useIsoCookies } from "hooks/useIsoCookies";
import {
  Grid,
  Link,
  Theme,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import memoize from "@RHCommerceDev/utils/memoize";
import { useEnv } from "hooks/useEnv";
import RHRHeader from "container-rhr-header";
import { v4 as uuidv4 } from "uuid";
import { useAppId } from "@RHCommerceDev/hooks/useAppId";

import { usePageContent } from "custom-providers/LocationProvider";
import useIsHomePage from "hooks/useIsHomePage";
import { SKIP_TO_MAIN } from "@RHCommerceDev/resources/rh-header.json";
import { processEnvServer } from "@RHCommerceDev/hooks/useSsrHooks";

const hideRoutes = [
  "/checkout/checkout-sign-in-register.jsp",
  "/checkout/signin.jsp",
  "/checkout/address.jsp",
  "/checkout/payment.jsp",
  "/checkout/update-payment.jsp",
  "/checkout/order_review.jsp",
  "/checkout/thank_you.jsp",
  "/checkout/confirmation.jsp",
  "/checkout/contract-payment.jsp",
  "/checkout/update-card.jsp"
];

export interface HeaderSwitcherProps {
  RHRHeaderProps?: RHRHeaderProps;
  isAem?: boolean;
  isInEditor?: boolean;
}

export const HeaderSwitcher: FC<HeaderSwitcherProps> = ({
  RHRHeaderProps,
  ...rest
}) => {
  const env = useEnv();
  const { isConcierge } = useAppId();
  const { pathname } = useLocation();
  const deviceType = useIsoCookies(["PF_EXP"])?.PF_EXP || "DESKTOP";
  const theme = useTheme();
  const { pageContent } = usePageContent();
  const isHomePage = useIsHomePage();
  // mobile device and < 992px on desktop
  const sm = useMediaQuery<Theme>(theme => {
    return theme.breakpoints.down("xl");
  });
  const [reloadKey, setReloadKey] = useState(null);

  const handleSkipToMainContent = e => {
    if (!processEnvServer) {
      e.preventDefault();
      const element = document.getElementById(
        isHomePage ? "component-link" : "main"
      );
      if (element && element.nextElementSibling && isHomePage) {
        element.nextElementSibling.scrollIntoView({
          behavior: "smooth",
          block: "center"
        });
        element.focus();
      } else if (element) {
        const focusableElements =
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
        const firstFocusableElement = element.querySelector(
          focusableElements
        ) as HTMLElement;
        if (firstFocusableElement) {
          firstFocusableElement.scrollIntoView({
            behavior: "smooth",
            block: "center"
          });
          firstFocusableElement.focus();
        }
      }
    }
  };

  useEffect(() => {
    // force a re-render on client side for problematic session

    if (!reloadKey) {
      setReloadKey(uuidv4());
    }
  }, []);

  return hideRoutes.includes(pathname) ? null : (
    <>
      {!isConcierge && (
        <Link
          component="button"
          underline="hover"
          onClick={handleSkipToMainContent}
          onKeyDown={e => e.key === "Enter" && handleSkipToMainContent(e)}
          className="!absolute !left-[-9999px] !z-[999] !bg-black !p-4 !text-white !opacity-0"
          tabIndex={0}
        >
          <Typography data-nosnippet>
            {pageContent?.HEADER_CONTENT?.SKIP_TO_PAGE_CONTENT ?? SKIP_TO_MAIN}
          </Typography>
        </Link>
      )}
      <Grid
        id="rh-header"
        container
        style={{
          zIndex: theme.zIndex.appBar + 1,
          position: "relative"
        }}
      >
        <>
          <>
            <RHRHeader
              {...RHRHeaderProps}
              {...rest}
              key={(reloadKey ? reloadKey : 123) + "header"}
              renderingFrom="headerSwitcher"
            />
          </>
        </>
      </Grid>
    </>
  );
};

export default memoize(HeaderSwitcher);
